export type MaskBlock = Digit | Separator | Character

export interface Digit {
  type: 'Digit'
  min?: number
  max?: number
}

export interface Separator {
  type: 'Separator'
  separator: string
}

export interface Character {
  type: 'Character'
  min?: number
  max?: number
}

/**
 * Common masks.
 */
export const masks = {
  americanDate: [
    {
      type: 'Digit',
      max: 2,
    },
    { type: 'Separator', separator: '/' },
    {
      type: 'Digit',
      max: 2,
    },
    { type: 'Separator', separator: '/' },
    {
      type: 'Digit',
      max: 4,
    },
  ],
  ssnOrItin: [
    {
      type: 'Digit',
      min: 3,
      max: 3,
    },
    { type: 'Separator', separator: '-' },
    {
      type: 'Digit',
      min: 2,
      max: 2,
    },
    { type: 'Separator', separator: '-' },
    {
      type: 'Digit',
      min: 4,
      max: 4,
    },
  ],
  ein: [
    {
      type: 'Digit',
      min: 2,
      max: 2,
    },
    { type: 'Separator', separator: '-' },
    {
      type: 'Digit',
      min: 7,
      max: 7,
    },
  ],
} as const satisfies Record<string, MaskBlock[]>

/**
 * Formats the given value using the specified mask.
 *
 * @param mask
 * @param value
 */
export function formatMask(mask: MaskBlock[], value: string) {
  const result: string[] = []

  let maskIndex = 0
  let valueIndex = 0

  while (maskIndex < mask.length && valueIndex < value.length) {
    const maskBlock = mask[maskIndex++]

    switch (maskBlock.type) {
      case 'Character': {
        scanBlockValue(/[a-zA-Z]/, maskBlock.min, maskBlock.max)
        break
      }
      case 'Digit': {
        scanBlockValue(/[0-9]/, maskBlock.min, maskBlock.max)
        break
      }
      case 'Separator': {
        const valueChar = value[valueIndex]
        if (maskBlock.separator === valueChar) {
          valueIndex++
        }
        result.push(maskBlock.separator)
        break
      }
    }
  }

  return result.join('')

  /**
   * Helper function for scanning a mask block value.
   *
   * @param regex The regex to check each character against.
   * @param min Minimum amount of characters
   * @param max Maximum amount of characters
   */
  function scanBlockValue(regex: RegExp, min = 1, max = 1) {
    let consumed = 0
    while (valueIndex < value.length && consumed < max) {
      const valueChar = value[valueIndex]
      if (regex.test(valueChar)) {
        valueIndex++
        result.push(valueChar)
        consumed++
        continue
      }

      // If we've reached the minimum number of digits, end
      // the current digit.
      if (consumed >= min) {
        break
      }

      // Make sure we always advance
      valueIndex++
    }
  }
}
